import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, Zap, LayoutDashboard, FolderKanban, ClipboardList, CheckSquare, Users, CreditCard, ShoppingCart, Activity, FileText, BookOpen, Shield, Radio, MessageSquare, Brain, User } from 'lucide-react';
import { Sheet, SheetTrigger } from '@/components/ui/sheet';
import api from '@/utils/api';

const LONG_PRESS_MS = 500;

// Map route → icon for the quick-shortcut popover
const ROUTE_ICONS = {
  '/dashboard': LayoutDashboard,
  '/projects': FolderKanban,
  '/forms': ClipboardList,
  '/tasks': CheckSquare,
  '/teams': Users,
  '/signatures': FileText,
  '/billing': CreditCard,
  '/marketplace': ShoppingCart,
  '/signal-check': Radio,
  '/signal-dashboard': Activity,
  '/workers-voice': MessageSquare,
  '/intelligence': Brain,
  '/users': Users,
  '/settings': Menu,
  '/profile': User,
  '/reports': FileText,
  '/guide': BookOpen,
  '/iso45001': Shield,
};

const ROUTE_LABELS = {
  '/dashboard': 'Dashboard',
  '/projects': 'Company',
  '/forms': 'Forms',
  '/tasks': 'Tasks',
  '/teams': 'Teams',
  '/signatures': 'Signatures',
  '/billing': 'Billing',
  '/marketplace': 'Marketplace',
  '/signal-check': 'Signal Check',
  '/signal-dashboard': 'Alignment',
  '/workers-voice': 'Workers Voice',
  '/intelligence': 'Intelligence',
  '/users': 'Users',
  '/settings': 'Settings',
  '/profile': 'Profile',
  '/reports': 'Reports',
  '/guide': 'User Guide',
  '/iso45001': 'ISO 45001',
  '/executive-reports': 'Executive Intel',
  '/compliance-dashboard': 'Compliance',
  '/leadership': 'Leadership',
  '/merit-demerit': 'Merit/Demerit',
  '/odsi': 'ODSI',
};

export default function MenuLongPress({ children: sheetContent, open, onOpenChange }) {
  const navigate = useNavigate();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [topPages, setTopPages] = useState([]);
  const pressTimer = useRef(null);
  const didLongPress = useRef(false);

  const startPress = async () => {
    didLongPress.current = false;
    pressTimer.current = setTimeout(async () => {
      didLongPress.current = true;
      // Haptic — iOS/Android where supported
      if (navigator.vibrate) navigator.vibrate(15);
      try {
        const res = await api.get('/audit/my-top-pages?limit=3');
        setTopPages(res.data?.top_pages || []);
      } catch {
        setTopPages([]);
      }
      setShortcutsOpen(true);
    }, LONG_PRESS_MS);
  };

  const cancelPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  // If the long-press triggered, swallow the subsequent click so the Sheet doesn't open
  const handleClickCapture = (e) => {
    if (didLongPress.current) {
      e.preventDefault();
      e.stopPropagation();
      didLongPress.current = false;
    }
  };

  const handleShortcutClick = (path) => {
    setShortcutsOpen(false);
    navigate(path);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            data-testid="mobile-menu-button"
            onPointerDown={startPress}
            onPointerUp={cancelPress}
            onPointerLeave={cancelPress}
            onPointerCancel={cancelPress}
            onClickCapture={handleClickCapture}
          >
            <Menu className="w-5 h-5" />
          </Button>
        </SheetTrigger>
        {sheetContent}
      </Sheet>

      {/* Long-press shortcut popover */}
      {shortcutsOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-150"
          onClick={() => setShortcutsOpen(false)}
          data-testid="longpress-shortcuts-overlay"
        >
          <div
            className="w-full max-w-sm bg-background border rounded-2xl shadow-2xl p-4 animate-in slide-in-from-bottom duration-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="longpress-shortcuts-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-amber-500" />
              <p className="text-sm font-semibold">Your most-visited pages</p>
            </div>
            {topPages.length === 0 ? (
              <p className="text-xs text-muted-foreground py-6 text-center">
                Not enough history yet. Visit a few pages then long-press again.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {topPages.map((p) => {
                  const Icon = ROUTE_ICONS[p.path] || Menu;
                  const label = ROUTE_LABELS[p.path] || p.path.replace('/', '');
                  return (
                    <button
                      key={p.path}
                      onClick={() => handleShortcutClick(p.path)}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl border bg-muted/30 hover:bg-muted active:scale-95 transition-all"
                      data-testid={`longpress-shortcut-${p.path.replace(/\//g, '-')}`}
                    >
                      <Icon className="w-6 h-6 text-indigo-600" />
                      <span className="text-[11px] font-medium text-center leading-tight">{label}</span>
                      <span className="text-[9px] text-muted-foreground">×{p.count}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-3 text-center">
              Tip: long-press the menu icon anywhere for quick shortcuts.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
